import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, makeStyles, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { RoleAssignmentMode } from '~/types';
import useTaskRoleAssignments, { PAGE_SIZE } from './useTaskRoleAssignments';
import RolesDisplayList from './RolesDisplayList';

const MODE_LABEL_IDS = {
  [RoleAssignmentMode.Inherit]: 'selectTaskRolesDialog.taskRoles',
  [RoleAssignmentMode.UserProfileRoles]:
    'selectTaskRolesDialog.userProfileRoles',
  [RoleAssignmentMode.SpecificRoles]: 'selectTaskRolesDialog.specificRoles'
};

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(1)
  },
  paginationRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0)
  },
  inheritedLabel: {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    flexGrow: 1
  },
  paginationLabel: {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5)
  },
  emptyText: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 0)
  }
}));

const TaskRolesList = ({ taskUri }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [page, setPage] = useState(1);

  const {
    roles,
    loading,
    hasNextPage,
    roleAssignmentMode
  } = useTaskRoleAssignments({ taskUri, page, pageSize: PAGE_SIZE });

  const modeLabel = roleAssignmentMode
    ? formatMessage({
        id:
          MODE_LABEL_IDS[roleAssignmentMode] ??
          'selectTaskRolesDialog.taskRoles'
      })
    : null;

  const startIndex = (page - 1) * PAGE_SIZE + 1;
  const endIndex = startIndex + roles.length - 1;

  const handlePrev = useCallback(() => setPage(p => Math.max(1, p - 1)), []);
  const handleNext = useCallback(() => setPage(p => p + 1), []);

  if (loading) {
    return (
      <div className={classes.root}>
        <Skeleton height={32} />
        <Skeleton height={32} />
        <Skeleton height={32} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {roles.length > 0 ? (
        <>
          <div className={classes.paginationRow}>
            {modeLabel && (
              <Typography className={classes.inheritedLabel}>
                <FormattedMessage
                  id="selectTaskRolesDialog.roleSettingsInherited"
                  values={{ mode: modeLabel }}
                />
              </Typography>
            )}
            <Typography className={classes.paginationLabel}>
              {startIndex}–{endIndex}
            </Typography>
            <IconButton size="small" onClick={handlePrev} disabled={page === 1}>
              <ChevronLeft fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleNext}
              disabled={!hasNextPage}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </div>
          <RolesDisplayList roles={roles} />
        </>
      ) : modeLabel ? (
        <Typography className={classes.inheritedLabel}>
          <FormattedMessage
            id="selectTaskRolesDialog.roleSettingsInherited"
            values={{ mode: modeLabel }}
          />
        </Typography>
      ) : (
        <Typography className={classes.emptyText}>
          <FormattedMessage id="selectTaskRolesDialog.noTaskRoles" />
        </Typography>
      )}
    </div>
  );
};

TaskRolesList.propTypes = {
  taskUri: PropTypes.string.isRequired
};

export default TaskRolesList;
